import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import AppLayout from "../components/layout/AppLayout";
import BreadcrumbNav from "../components/layout/BreadcrumbNav";
import type { PickToLight } from "../types";
import { apiClient } from "../utils/api";
import { useWebSocket } from "../utils/useWebSocket";

interface PtlRealtimeEvent {
  type: string;
  data?: any;
  timestamp: number;
}

const PickToLightPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<PickToLight | null>(null);
  const [realtimeEvents, setRealtimeEvents] = useState<
    Map<string, PtlRealtimeEvent>
  >(new Map());
  const [form] = Form.useForm();

  const {
    data: ptls = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["pickToLights"],
    queryFn: () => apiClient.getPickToLights(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<PickToLight>) =>
      apiClient.createPickToLight(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pickToLights"] });
      message.success("Created successfully");
    },
    onError: () => {
      message.error("Failed to save");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PickToLight> }) =>
      apiClient.updatePickToLight(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pickToLights"] });
      message.success("Updated successfully");
    },
    onError: () => {
      message.error("Failed to save");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deletePickToLight(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pickToLights"] });
      message.success("Deleted successfully");
    },
    onError: () => {
      message.error("Failed to delete");
    },
  });

  const { connected, connectionStatus, subscribe } = useWebSocket(undefined, {
    deviceType: 'ptl',
    onMessage: (msg) => {
      if (
        (msg.type === "ptl_event" ||
          msg.type === "ptl_confirm" ||
          msg.type === "ptl_button_press" ||
          msg.type === "ptl_raw_response") &&
        msg.device_id
      ) {
        setRealtimeEvents((prev) => {
          const newMap = new Map(prev);
          newMap.set(msg.device_id!, {
            type: msg.type,
            data: msg.data,
            timestamp: msg.timestamp ?? Date.now() / 1000,
          });
          return newMap;
        });
      }
    },
    onConnect: () => {
      message.success("PTL real-time updates connected");
    },
  });

  useEffect(() => {
    if (connected && ptls.length > 0) {
      const deviceIds = ptls.map((p) => p.device_id);
      subscribe(deviceIds);
    }
  }, [connected, ptls, subscribe]);

  const handleEdit = (item: PickToLight) => {
    setEditingItem(item);
    form.setFieldsValue(item);
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
      setModalVisible(false);
    } catch (error) {
      console.error("Failed to save:", error);
    }
  };

  const columns: ColumnsType<PickToLight> = [
    {
      title: "Device ID",
      dataIndex: "device_id",
      key: "device_id",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "online" ? "green" : "red"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
    },
    {
      title: "Last Event",
      key: "last_event",
      render: (_, record) => {
        const event = realtimeEvents.get(record.device_id);
        if (!event) return <span style={{ color: '#999' }}>-</span>;
        const isStale = Date.now() / 1000 - event.timestamp > 10;
        const labelMap: Record<string, string> = {
          ptl_event: "Event",
          ptl_confirm: "Confirm",
          ptl_button_press: "Button Press",
          ptl_raw_response: "Response",
        };
        return (
          <Space>
            <Tag color={isStale ? "default" : "blue"}>
              {labelMap[event.type] || event.type}
            </Tag>
            {!isStale && <Tag color="blue">LIVE</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this device?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout
      title="Pick To Light Management"
      extraActions={
        <Space>
          <Tooltip title={`Real-time updates: ${connectionStatus}`}>
            <div className="flex items-center gap-4">
              <Badge
                status={
                  connectionStatus === "connected"
                    ? "processing"
                    : connectionStatus === "connecting"
                    ? "warning"
                    : "error"
                }
              />{" "}
              {connectionStatus === "connected"
                ? "Live"
                : connectionStatus === "connecting"
                ? "Connecting..."
                : "Offline"}
            </div>
          </Tooltip>
          <Tooltip title="Refresh data">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
            />
          </Tooltip>
        </Space>
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <BreadcrumbNav />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Device
        </Button>
      </div>
      <Card>
        <Table
          columns={columns}
          dataSource={ptls}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 700 }}
        />
      </Card>

      <Modal
        title={editingItem ? "Edit Device" : "Add Device"}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="device_id"
            label="Device ID"
            rules={[{ required: true, message: "Please enter device ID" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="offline">
            <Input />
          </Form.Item>
          <Form.Item name="ip_address" label="IP Address">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
};

export default PickToLightPage;
